import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Page from "components/base/Page";
import { OrderStatusChain } from "components/base/Status";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import SyncPriceToPriceListView from "pages/orderScheduler/SyncPriceToPriceListView";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BaseInfoView from "./BaseInfoView";
import { SALES_DELETE_ORDER, SAVE_ORDER } from "./graphql";
import OdooInfoView from "./OdooInfoView";
import { OrderContext } from "./OrderDetail";
import ProductsView from "./ProductsView";
import UpdateStateView from "./UpdateStateView";

const OrderForm = ({
  originalOrder,
  stockCategories,
  activeCurrencies,
  odooPricelists,
  productStocks,
}) => {
  const [order, setOrder] = useState(originalOrder);

  const [saveOrder, saveOrderRes] = useMutation(SAVE_ORDER, {
    variables: {
      id: originalOrder.id,
      input: {
        stockCategoryId: order.stockCategoryId,
        customerId: order.customer ? order.customer.id : null,
        notes: order.notes,

        exchangeRate: order.exchangeRate,

        odooPricelistId: order.odooPricelistId,
        odooSalesOrderClientOrderRef: order.odooSalesOrderClientOrderRef,
        odooPaymentTermsId: order.odooPaymentTermsId,
        odooTaxId: order.odooTaxId,
        odooIncoterm: order.odooIncoterm,
        odooXDeliverymethod: order.odooXDeliverymethod,
        odooXDoMinDate: order.odooXDoMinDate || null,
        odooUserId: order.odooUserId,
        autoSyncToOdoo: order.autoSyncToOdoo,
      },
      orderLines: order.orderLines
        .filter((i) => i.product)
        .map((i) => ({
          productId: i.product.id,
          qty: i.qty,
          salesPrice: i.salesPrice,
          discountPercentage: i.discountPercentage,
        })),
    },
    onCompleted() {
      Alert("success", "Order saved");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const navigate = useNavigate();
  const [deleteOrder, deleteOrderRes] = useMutation(SALES_DELETE_ORDER, {
    variables: { id: originalOrder.id },
    onCompleted() {
      Alert("success", "Order deleted");
      navigate("./..");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const [changeWarehouse, changeWarehouseRes] = useMutation(SAVE_ORDER, {
    onError(error) {
      Alert("error", parseError(error));
    },
    onCompleted() {
      Alert("success", "Warehouse changed successfully");
      setOrder((prev) => ({ ...prev, orderLines: [] }));
    },
  });

  const [changePricelist, changePricelistRes] = useMutation(SAVE_ORDER, {
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const modal = useModals();
  const syncPriceModal = useModals();

  function updateState(operation) {
    modal.present({
      title: "Update Order State",
      center: true,
      maxWidth: "max-w-3xl",
      children: (
        <UpdateStateView
          id={originalOrder.id}
          warehouseName={originalOrder.warehouseName}
          operation={operation}
          hide={modal.hide}
        />
      ),
    });
  }

  function syncBackToPriceListHandler() {
    if (!originalOrder.pricelist) {
      Alert("error", "No price list associated with this order");
      return;
    }

    syncPriceModal.present({
      title: "Sync Price",
      subtitle: "Sync Price in this order back to Price List",
      maxWidth: "max-w-6xl",
      children: (
        <SyncPriceToPriceListView
          priceListId={originalOrder.pricelist.id}
          lines={order.orderLines.filter((i) => i.product)}
        />
      ),
    });
  }

  function deleteOrderHandler() {
    if (
      window.confirm(
        "Are you sure you want to delete this order? Note that this would not deleted the order created in Odoo if already synced.",
      )
    ) {
      deleteOrder();
    }
  }

  const validStocks =
    originalOrder.stockCategory?.stocks.filter((i) => i.product.odooId) || [];

  return (
    <Page
      title={`Sales Order #${originalOrder.id}`}
      backTo={"./.."}
      rightButtons={
        <div className="flex space-x-6">
          <a
            href={`${process.env.REACT_APP_SERVER_ADMIN_URL}orders/order/${originalOrder.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Admin
          </a>
          <Button onClick={syncBackToPriceListHandler}>
            Sync Price back to Price List
          </Button>
          {originalOrder.state === "DRAFT" && (
            <Button onClick={() => updateState("START_PLANNING")}>
              Start Planning
            </Button>
          )}
          {originalOrder.state === "PLANNING" && (
            <Button onClick={() => updateState("RESET_TO_DRAFT")}>
              Reset to Draft
            </Button>
          )}
          <Button onClick={saveOrder} loading={saveOrderRes.loading}>
            Save
          </Button>
        </div>
      }
    >
      <div className="p-6">
        <OrderStatusChain status={originalOrder.state} />
      </div>

      <OrderContext.Provider
        value={{
          originalOrder,
          order,
          setOrder,
          saveOrder,
          changeWarehouse,
          changeWarehouseRes,
          changePricelist,
          changePricelistRes,
          validStocks,
          productStocks,
        }}
      >
        <div className="grid gap-6 grid-cols-12 p-6 pt-0 items-stretch">
          <div className="col-span-9 space-y-6">
            <BaseInfoView
              stockCategories={stockCategories}
              activeCurrencies={activeCurrencies}
              odooPricelists={odooPricelists}
            />
          </div>

          <div className="col-span-3 card">
            <OdooInfoView />
          </div>

          <div className=" col-span-full">
            <ProductsView odooPricelists={odooPricelists} />
          </div>

          <div className="col-span-full flex justify-between space-x-6">
            <div className="pt-0 opacity-70">
              This Sales Order page is designed for order handling for AB
              warehouses orders. It will take the stocks managed by AB as our
              stock source. Once the order start planning, the stock will be
              reserved in the Stock Categories. Margin is calculated based on
              the cost from Odoo product.
            </div>

            <div className="flex justify-end whitespace-nowrap">
              <Button
                danger
                onClick={deleteOrderHandler}
                loading={deleteOrderRes.loading}
              >
                Delete Order
              </Button>
            </div>
          </div>
        </div>
      </OrderContext.Provider>
    </Page>
  );
};
export default OrderForm;
